/* eslint-disable no-console */

import { Box, Button, Chip, Divider, Fade, Modal, Paper, Radio, Stack, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { silobagDataDetailService } from '@old/services/domain/base/silobag/base/silobag-data-detail';
import { PropsWithChildren, useState } from 'react';
import i18n from '../../../../libs/language';
import dayjs from 'dayjs';
import { useEffectOnUpdate } from '@old/hooks/core';
import DownloadIcon from '@old/assets/svg/core/download-alt-blue.svg';
import { configButtonStylesContained } from '@old/styles/generic-styles';
import location from '../../../../assets/svg/location_searching.svg';

type Poe2Summary = Awaited<
  ReturnType<typeof silobagDataDetailService>
>['summary']['silobags_history'][0]['poe_v2'];

type PoeModalProps = {
  open: boolean;
  onClose: () => void;
  data?: Poe2Summary;
  poeId?: number | null;
};

type ImageType = {
  mainImageUrl: string | undefined;
  mainLat: string | null | undefined;
  mainLon: string | null | undefined;
  mainAccuracy: string | null | undefined;
  mainTimestamp: string | null | undefined;
  mainAzimuth?: number | string | undefined;
  mainLabel: string | undefined;
  breakageMotiveID?: number;
};

export function PoeV2SilobagModal({ open, onClose, data }: PoeModalProps) {
  const [listIDX, setListIDX] = useState(0);
  const [imageTitle, setImageTitle] = useState(i18n.t('app.silobag_history.init_photo'));
  const [showLegend, setShowLegend] = useState(true);

  useEffectOnUpdate(() => {
    setImageTitle(finalArray[listIDX]?.mainLabel || '');
  }, [listIDX]);

  const initImage: ImageType = {
    mainImageUrl: data?.init_image_url,
    mainLat: data?.init_lat,
    mainLon: data?.init_lon,
    mainAccuracy: data?.init_accuracy,
    mainTimestamp: data?.init_timestamp,
    mainLabel: i18n.t('app.silobag_history.init_photo'),
  };

  const mainImage: ImageType = {
    mainImageUrl: data?.main_image_url,
    mainLat: data?.main_lat,
    mainLon: data?.main_lon,
    mainAccuracy: data?.main_accuracy,
    mainTimestamp: data?.main_timestamp,
    mainLabel: i18n.t('app.silobag_history.general_photo'),
  };

  const endImage: ImageType = {
    mainImageUrl: data?.end_image_url,
    mainLat: data?.end_lat,
    mainLon: data?.end_lon,
    mainAccuracy: data?.end_accuracy,
    mainTimestamp: data?.end_timestamp,
    mainLabel: i18n.t('app.silobag_history.end_image'),
  };

  const breakageDetailsData: ImageType[] =
    data?.poe_v2_breakage_images
      .map((rot) => ({
        mainImageUrl: rot.image_url,
        mainLat: rot.lat,
        mainLon: rot.lon,
        mainAccuracy: rot.accuracy,
        mainTimestamp: rot.timestamp,
        mainLabel: i18n.t('app.silobag_history.photo_breakage'),
        breakageMotiveID: rot.breakage_motives.id,
      }))
      .flat()
      .filter((data) => data.mainImageUrl !== null) || [];

  // TODO: Revisar con datos completos si el back devuelve asi la data
  const qualityDetailsData: ImageType[] =
    data?.poe_v2_quality_revisions
      .map((rot) => ({
        mainImageUrl: rot.image_url,
        mainLat: rot.lat,
        mainLon: rot.lon,
        mainAccuracy: rot.accuracy,
        mainTimestamp: rot.timestamp,
        mainLabel: i18n.t('app.silobag_history.medition_image'),
      }))
      .flat()
      .filter((data) => data.mainImageUrl !== null) || [];

  const devicesDetailsData: ImageType[] =
    (data?.poe_sbd_states ?? [])
      .map((rot) => ({
        mainImageUrl: rot.image_url,
        mainLat: rot.lat,
        mainLon: rot.lon,
        mainAccuracy: rot.accuracy,
        mainTimestamp: rot.timestamp,
        mainLabel: i18n.t('app.silobag_history.device_image'),
      }))
      .flat()
      .filter((item) => item.mainImageUrl !== null) || [];

  const extraImagesData: ImageType[] =
    data?.poe_v2_poe_extra_images
      .map((rot) => ({
        mainImageUrl: rot.image_url,
        mainLat: rot.lat,
        mainLon: rot.lon,
        mainAccuracy: rot.accuracy,
        mainTimestamp: rot.timestamp,
        mainLabel: i18n.t('app.silobag_history.photo_extra'),
      }))
      .flat()
      .filter((data) => data.mainImageUrl !== null) || [];

  const finalArray: ImageType[] = [
    initImage?.mainImageUrl ? initImage : null,
    mainImage?.mainImageUrl ? mainImage : null,
    endImage?.mainImageUrl ? endImage : null,
    ...breakageDetailsData,
    ...qualityDetailsData,
    ...devicesDetailsData,
    ...extraImagesData,
  ].filter(Boolean) as ImageType[];

  const SCROLL = {
    toLeft: () => setListIDX((prev) => (prev - 1 + finalArray.length) % finalArray.length),
    toRight: () => setListIDX((prev) => (prev + 1) % finalArray.length),
  };

  const STATUS_GENERAL: Record<number, string> = {
    1: i18n.t('app.silobag_history.Optimum'),
    2: i18n.t('app.silobag_history.precaution'),
    3: i18n.t('app.silobag_history.high_risk'),
    4: i18n.t('app.silobag_history.critical'),
  };

  const LANCE_POSITIONS: Record<number, string> = {
    4: i18n.t('app.silobag_devices.init'),
    5: i18n.t('app.silobag_devices.init_middle'),
    6: i18n.t('app.silobag_devices.middle'),
    7: i18n.t('app.silobag_devices.middle_end'),
    8: i18n.t('app.silobag_devices.end'),
  };

  const STATUS_COLORS: Record<number, string> = {
    1: '#6BAA00',
    2: '#FFDD00',
    3: '#FF7F27',
    4: '#ED0909',
  };

  const statusId = data?.valid;
  const statusText = statusId ? 'Válida' : 'Inválida';
  const statusColor = statusId ? '#6BAA00' : '#ED0909';
  const hasBreakage = data && data?.poe_v2_breakage_images?.length > 0;
  const silobagId = data ? data.silobag_id : null;
  const pascalCaseLabel = finalArray[listIDX]?.mainLabel?.replace(/ /g, '_');
  const formattedDate = finalArray[listIDX]?.mainTimestamp
    ? dayjs(Number(finalArray[listIDX]?.mainTimestamp)).format('DD_MM_YYYY-HH_mm')
    : '';

  const isValid = data?.valid;
  const distance = data?.distance_to_sb_polygon;
  const accuracy = data?.main_accuracy;

  const totalDistance = (((distance ?? 0) + (parseFloat(accuracy ?? '0') ?? 0)) / 1000).toFixed(2);

  const getWarningMessage = () => {
    if (totalDistance && isValid === null) {
      return i18n.t('app.silobag_history.poe_invalid');
    }
    if (isValid === false) {
      return i18n.t('app.silobag_history.poe_warning_description', { totalDistance: totalDistance ?? 0 });
    }
    return null;
  };

  const warningMessage = getWarningMessage();

  return (
    <MyModal open={open} onClose={onClose}>
      {!data ? (
        <Typography>No hay datos</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography fontSize={28} fontWeight={800}>
                {i18n.t('app.silobag_history.detail_poe')}
              </Typography>
              <Chip
                label={statusText}
                icon={
                  <Box
                    component="div"
                    sx={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      bgcolor: statusColor,
                    }}
                  />
                }
                sx={{
                  bgcolor: `${statusColor}20`,
                  color: statusColor,
                  fontWeight: 'bold',
                  border: `1.5px solid ${statusColor}`,
                  borderRadius: '6px',
                  fontSize: '11px',
                  height: '24px',
                }}
              />
            </Stack>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Stack direction="row" spacing={8} flexWrap="wrap">
              <Box>
                <Chip
                  label={i18n.t('app.silobag_data.lat')}
                  size="small"
                  sx={{
                    bgcolor: '#f5f5f5',
                    color: '#424242',
                    border: '1px solid #d0d0d0',
                    borderRadius: '6px',
                    fontSize: '11px',
                    height: '22px',
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                />
                <Typography variant="body2" fontSize={13}>
                  {data.init_lat}
                </Typography>
              </Box>
              <Box>
                <Chip
                  label={i18n.t('app.silobag_data.long')}
                  size="small"
                  sx={{
                    bgcolor: '#f5f5f5',
                    color: '#424242',
                    border: '1px solid #d0d0d0',
                    borderRadius: '6px',
                    fontSize: '11px',
                    height: '22px',
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                />
                <Typography variant="body2" fontSize={13}>
                  {data.init_lon}
                </Typography>
              </Box>
              <Box>
                <Chip
                  label={i18n.t('app.silobag_history.gps_accuracy')}
                  size="small"
                  sx={{
                    bgcolor: '#f5f5f5',
                    color: '#424242',
                    border: '1px solid #d0d0d0',
                    borderRadius: '6px',
                    fontSize: '11px',
                    height: '22px',
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                />
                <Typography variant="body2" fontSize={13}>
                  {parseFloat(data.init_accuracy).toFixed(2)} mts
                </Typography>
              </Box>
              <Box>
                <Chip
                  label={i18n.t('app.date')}
                  size="small"
                  sx={{
                    bgcolor: '#f5f5f5',
                    color: '#424242',
                    border: '1px solid #d0d0d0',
                    borderRadius: '6px',
                    fontSize: '11px',
                    height: '22px',
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                />
                <Typography variant="body2" fontSize={13}>
                  {dayjs(Number(data.init_timestamp)).format('hh:mm — DD/MM/YYYY')}
                </Typography>
              </Box>
            </Stack>
          </Stack>
          {warningMessage && (
            <Typography variant="body2" style={{ color: '#E30000', fontSize: '0.8rem' }} marginLeft="0.5rem">
              {warningMessage.includes('app.silobag_history.poe_warning_description') && (
                <img src={location} alt="release-icon" />
              )}
              {warningMessage}
            </Typography>
          )}
          <Divider />
          {/* EVALUACION GENERAL */}
          <Stack spacing={4} direction={{ xs: 'column', md: 'row' }}>
            <Box width={{ xs: '100%', md: '65%' }}>
              {/* DETALLES DE RECORRIDA */}
              <Stack spacing={2} width="100%" height="100%" display="flex" flexDirection="column">
                {/* OBSERVACIONES DE RECORRIDA */}
                <Paper
                  elevation={3}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    background: `linear-gradient(135deg, ${
                      STATUS_COLORS[data.status_id]
                    }18 0%, #ffffff 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${STATUS_COLORS[data.status_id]} 0%, ${
                        STATUS_COLORS[data.status_id]
                      }80 100%)`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={4}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography fontSize={18} fontWeight={800}>
                        {i18n.t('app.silobag_history.general_evaluation')}
                      </Typography>
                      <Chip
                        label={STATUS_GENERAL[data.status_id]}
                        icon={
                          <Box
                            component="div"
                            sx={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '6px',
                              bgcolor: STATUS_COLORS[data.status_id],
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': {
                                  opacity: 1,
                                  transform: 'scale(1)',
                                },
                                '50%': {
                                  opacity: 0.5,
                                  transform: 'scale(1.2)',
                                },
                              },
                            }}
                          />
                        }
                        sx={{
                          bgcolor: `${STATUS_COLORS[data.status_id]}20`,
                          color: STATUS_COLORS[data.status_id],
                          border: `1px solid ${STATUS_COLORS[data.status_id]}`,
                          borderRadius: '6px',
                          fontSize: '11px',
                          height: '24px',
                        }}
                      />
                    </Stack>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography color="textSecondary" fontSize={11}>
                        {i18n.t('app.silobag_history.description_election')}
                      </Typography>
                      <Typography fontSize={13} fontStyle="italic" color="text.primary">
                        {data.observations ?? '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
                {/* SECCION PREGUNTAS */}
                <Box>
                  <Divider sx={{ mb: 2, mt: 1 }} textAlign="left">
                    <Chip
                      label={i18n.t('app.silobag_history.breakage_register')}
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        color: '#424242',
                        border: '1px solid #d0d0d0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        height: '22px',
                        fontWeight: 500,
                      }}
                    />
                  </Divider>
                  {/* ROTURAS */}
                  <Stack direction="row" spacing={3} alignItems="center" mb={2}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Radio checked={hasBreakage} sx={{ pointerEvents: 'none' }} size="small" />
                        <Typography
                          fontSize={14}
                          fontWeight="medium"
                          color="text.primary"
                          style={{ userSelect: 'none' }}
                        >
                          {i18n.t('app.yes')}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Radio checked={!hasBreakage} sx={{ pointerEvents: 'none' }} size="small" />
                        <Typography
                          fontSize={14}
                          fontWeight="medium"
                          color="text.primary"
                          style={{ userSelect: 'none' }}
                        >
                          {i18n.t('app.no')}
                        </Typography>
                      </Stack>
                      <Divider
                        orientation="vertical"
                        flexItem
                        style={{ height: '24px', alignSelf: 'center' }}
                      />
                      <Typography fontSize={14} color="textSecondary">
                        {i18n.t('app.silobag_history.has_breakage')}
                      </Typography>
                    </Stack>
                    {/* MOTIVO DE ROTURAS */}
                    {hasBreakage && (
                      <>
                        <Divider
                          orientation="vertical"
                          flexItem
                          style={{ height: '24px', alignSelf: 'center' }}
                        />
                        <Stack direction="row" flexWrap="wrap" gap={1} flex={1}>
                          {data.poe_v2_breakage_images.map((breakage, index) => {
                            const currentPhotoBreakageMotiveID = finalArray[listIDX]?.breakageMotiveID;
                            const isCurrentBreakage =
                              breakage.breakage_motives.id === currentPhotoBreakageMotiveID;

                            return (
                              <Chip
                                key={index}
                                label={breakage.breakage_motives.description}
                                size="small"
                                sx={{
                                  bgcolor: isCurrentBreakage ? '#e3f2fd' : '#f5f5f5',
                                  color: isCurrentBreakage ? '#1976d2' : '#9e9e9e',
                                  border: isCurrentBreakage ? '1px solid #90caf9' : '1px solid #e0e0e0',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  height: '22px',
                                  fontWeight: isCurrentBreakage ? 600 : 500,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    bgcolor: isCurrentBreakage ? '#e3f2fd' : '#f5f5f5',
                                  },
                                }}
                              />
                            );
                          })}
                        </Stack>
                      </>
                    )}
                  </Stack>

                  <Divider sx={{ my: 2 }} textAlign="left">
                    <Chip
                      label={i18n.t('app.silobag_history.custom_questions')}
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        color: '#424242',
                        border: '1px solid #d0d0d0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        height: '22px',
                        fontWeight: 500,
                      }}
                    />
                  </Divider>
                  {/* PREGUNTAS PERSONALIZADAS */}
                  <Stack spacing={1.5} overflow="auto" maxHeight="200px">
                    {data.poe_v2_details.map((question, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Radio checked={question.result} sx={{ pointerEvents: 'none' }} size="small" />
                          <Typography
                            fontSize={14}
                            fontWeight="medium"
                            color="text.primary"
                            style={{ userSelect: 'none' }}
                          >
                            {i18n.t('app.yes')}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Radio checked={!question.result} sx={{ pointerEvents: 'none' }} size="small" />
                          <Typography
                            fontSize={14}
                            fontWeight="medium"
                            color="text.primary"
                            style={{ userSelect: 'none' }}
                          >
                            {i18n.t('app.no')}
                          </Typography>
                        </Stack>
                        <Divider
                          orientation="vertical"
                          flexItem
                          style={{ height: '24px', alignSelf: 'center' }}
                        />
                        <Typography fontSize={14} color="textSecondary">
                          {question.question_description}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
                {/* SPACER PARA EMPUJAR ELEMENTOS AL FONDO */}
                <Box flex={1} />
                {/* LANZAS */}
                <Paper
                  elevation={1}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    background: 'linear-gradient(135deg, #f5f5f520 0%, #ffffff 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #9e9e9e 0%, #9e9e9e70 100%)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={4}>
                    <Typography width="128px" fontSize={16} fontWeight="bold" color="text.secondary">
                      {i18n.t('app.silobag_history.lance_state')}
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography color="textSecondary" fontSize={11} mb={0.5}>
                        {i18n.t('app.silobag_history.description_election')}
                      </Typography>
                      <Typography fontSize={13} fontStyle="italic" color="text.primary">
                        {data.sbd_states_observations !== '' ? data.sbd_states_observations : '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
                {/* MEDICION DE HUMEDAD */}
                <Paper
                  elevation={1}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    background: 'linear-gradient(135deg, #f5f5f520 0%, #ffffff 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: 'linear-gradient(90deg, #9e9e9e 0%, #9e9e9e70 100%)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={4}>
                    <Typography width="128px" fontSize={16} fontWeight="bold" color="text.secondary">
                      {i18n.t('app.silobag_history.quality_revision.humidity_measurement')}
                    </Typography>
                    {data.poe_v2_quality_revisions.length !== 0 ? (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <Stack direction="row" spacing={3} flexWrap="wrap" alignItems="center">
                          {data.poe_v2_quality_revisions.map((revision, index) => (
                            <Stack key={index} direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={i18n.t('app.state_stock_page.position')}
                                size="small"
                                sx={{
                                  bgcolor: '#f5f5f5',
                                  color: '#424242',
                                  border: '1px solid #d0d0d0',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  height: '22px',
                                  mb: 0.5,
                                  fontWeight: 500,
                                }}
                              />
                              <Typography variant="body2" fontSize={13}>
                                {LANCE_POSITIONS[revision.silobag_positions_id] ?? '—'}
                              </Typography>
                              <Chip
                                label={i18n.t('app.silobag_history.quality_revision.value')}
                                size="small"
                                sx={{
                                  bgcolor: '#f5f5f5',
                                  color: '#424242',
                                  border: '1px solid #d0d0d0',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  height: '22px',
                                  mb: 0.5,
                                  fontWeight: 500,
                                }}
                              />
                              <Typography variant="body2" fontSize={13}>
                                {revision.value}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </>
                    ) : null}
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography color="textSecondary" fontSize={11} mb={0.5}>
                        {i18n.t('app.silobag_history.description_election')}
                      </Typography>
                      <Typography fontSize={13} fontStyle="italic" color="text.primary">
                        {data.quality_revisions_observations !== ''
                          ? data.quality_revisions_observations
                          : '—'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Stack>
            </Box>
            {/* PARTE DE LAS FOTOS */}
            <Box width={{ xs: '100%', md: '35%' }}>
              {finalArray && finalArray?.length > 0 ? (
                <>
                  <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                    <Chip
                      label={`Foto ${listIDX + 1}/${finalArray.length}`}
                      size="small"
                      sx={{
                        bgcolor: '#f5f5f5',
                        color: '#424242',
                        border: '1px solid #d0d0d0',
                        borderRadius: '6px',
                        fontSize: '11px',
                        height: '22px',
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={imageTitle}
                      size="small"
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1976d2',
                        border: '1px solid #90caf9',
                        borderRadius: '6px',
                        fontSize: '11px',
                        height: '22px',
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                  <Box style={{ width: '100%', height: '90%' }} component="div">
                    <Box className="slider-wrapper" component="div">
                      <Box
                        component="div"
                        className="slider keep-scrolling-without-scroll"
                        style={{ position: 'absolute', width: '100%' }}
                      >
                        {finalArray.map((image, i) => (
                          <img
                            ref={(node) => {
                              if (node && listIDX === i) {
                                node.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'nearest',
                                  inline: 'start',
                                });
                              }
                            }}
                            style={{ width: '100%', objectFit: 'fill', display: 'inline-block' }}
                            key={i}
                            src={typeof image === 'string' ? image : image?.mainImageUrl}
                            alt={'silobag-image-' + (i + 1)}
                          />
                        ))}
                      </Box>

                      {finalArray[listIDX] ? (
                        <>
                          <Box
                            component="div"
                            sx={{
                              position: 'absolute',
                              bottom: '0%',
                              left: '-2%',
                              width: '32px',
                              height: '32px',
                              borderRadius: '6px',
                              backgroundColor: 'rgba(255, 255, 255, 0.82)',
                              backdropFilter: 'blur(8px)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              border: '1px solid rgba(0, 0, 0, 0.1)',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              zIndex: 10,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              },
                            }}
                            onClick={() => setShowLegend(!showLegend)}
                          >
                            {showLegend ? (
                              <Visibility sx={{ color: '#6baa00', fontSize: '18px' }} />
                            ) : (
                              <VisibilityOff sx={{ color: '#9e9e9e', fontSize: '18px' }} />
                            )}
                          </Box>

                          {showLegend ? (
                            <Paper
                              elevation={3}
                              sx={{
                                position: 'absolute',
                                bottom: '3%',
                                left: '1.2%',
                                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                                backdropFilter: 'blur(8px)',
                                padding: '12px',
                                borderRadius: '12px',
                                maxWidth: '60%',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              }}
                            >
                              <Stack spacing={1}>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip
                                    label={i18n.t('app.silobag_data.lat')}
                                    size="small"
                                    sx={{
                                      bgcolor: '#e8f5e9',
                                      color: '#2e7d32',
                                      fontSize: '10px',
                                      height: '20px',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Typography variant="body2" fontSize={11} fontWeight="bold">
                                    {parseFloat(finalArray[listIDX].mainLat as string).toFixed(6) ?? '—'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip
                                    label={i18n.t('app.silobag_data.long')}
                                    size="small"
                                    sx={{
                                      bgcolor: '#e8f5e9',
                                      color: '#2e7d32',
                                      fontSize: '10px',
                                      height: '20px',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Typography variant="body2" fontSize={11} fontWeight="bold">
                                    {parseFloat(finalArray[listIDX].mainLon as string).toFixed(6) ?? '—'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip
                                    label={i18n.t('app.date')}
                                    size="small"
                                    sx={{
                                      bgcolor: '#e3f2fd',
                                      color: '#1565c0',
                                      fontSize: '10px',
                                      height: '20px',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Typography variant="body2" fontSize={11} fontWeight="bold">
                                    {finalArray[listIDX]?.mainTimestamp
                                      ? dayjs(Number(finalArray[listIDX]?.mainTimestamp)).format(
                                          'hh:mm — DD/MM/YYYY'
                                        )
                                      : i18n.t('app.water_mark.missing_date')}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                  <Chip
                                    label={i18n.t('app.water_mark.gps')}
                                    size="small"
                                    sx={{
                                      bgcolor: '#fff3e0',
                                      color: '#e65100',
                                      fontSize: '10px',
                                      height: '20px',
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Typography variant="body2" fontSize={11} fontWeight="bold">
                                    {parseFloat(data.init_accuracy).toFixed(2) ?? '—'} mts
                                  </Typography>
                                </Stack>
                                {finalArray[listIDX].mainAzimuth && (
                                  <Stack direction="row" spacing={1} flexWrap="wrap">
                                    <Chip
                                      label={i18n.t('app.water_mark.compass')}
                                      size="small"
                                      sx={{
                                        bgcolor: '#fce4ec',
                                        color: '#c2185b',
                                        fontSize: '10px',
                                        height: '20px',
                                        fontWeight: 600,
                                      }}
                                    />
                                    <Typography variant="body2" fontSize={11} fontWeight="bold">
                                      {finalArray[listIDX].mainAzimuth}°
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            </Paper>
                          ) : null}
                        </>
                      ) : null}

                      <Box
                        component="div"
                        sx={{
                          position: 'absolute',
                          top: '-2.5%',
                          right: '-2%',
                        }}
                      >
                        <DownloadLink
                          url={finalArray[listIDX]?.mainImageUrl ?? ''}
                          fileName={`${silobagId}-${pascalCaseLabel}-${formattedDate}`}
                        />
                      </Box>

                      <Paper
                        sx={{
                          borderRadius: '100%',
                          left: '0.5rem',
                          width: '30px',
                          height: '30px',
                          position: 'absolute',
                          top: '50%',
                          maxHeight: '30px',
                          fontSize: '2rem',
                          cursor: 'pointer',
                          userSelect: 'none',
                          transform: 'translateY(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&: hover': {
                            opacity: '80%',
                          },
                        }}
                        onClick={SCROLL.toLeft}
                      >
                        <Box style={{ opacity: '70%' }} component="div">
                          {'<'}
                        </Box>
                      </Paper>
                      <Paper
                        sx={{
                          borderRadius: '100%',
                          right: '0.5rem',
                          width: '30px',
                          height: '30px',
                          position: 'absolute',
                          top: '50%',
                          maxHeight: '30px',
                          fontSize: '2rem',
                          cursor: 'pointer',
                          userSelect: 'none',
                          transform: 'translateY(-50%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&: hover': {
                            opacity: '80%',
                          },
                        }}
                        onClick={SCROLL.toRight}
                      >
                        <Box style={{ opacity: '70%' }} component="div">
                          {'>'}
                        </Box>
                      </Paper>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        className="slider-nav"
                      >
                        {finalArray.map((_, i) => (
                          <Box
                            component="div"
                            key={i}
                            onClick={() => setListIDX(i)}
                            style={{
                              width: '13px',
                              backgroundColor: listIDX === i ? '#7A7A7A' : '#f3f2f7',
                              height: '13px',
                              borderRadius: '50%',
                              border: '1px solid #a7a7a7',
                            }}
                          ></Box>
                        ))}
                      </Stack>
                    </Box>
                  </Box>
                </>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" component="div">
                  <Typography>{i18n.t('app.silobag_data.existence_images')}</Typography>
                </Box>
              )}
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center">
            <Box flex={1} />
            <Button onClick={onClose} variant="contained" sx={configButtonStylesContained}>
              {i18n.t('app.close')}
            </Button>
          </Stack>
        </>
      )}
    </MyModal>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
};

function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100%',
    maxWidth: '80%',
    overflowX: 'hidden',
    maxHeight: '95vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} style={{ height: '100%' }}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style} component="div">
          {children}
        </Box>
      </Fade>
    </Modal>
  );
}

type DownloadLinkProps = {
  url: string;
  fileName?: string;
};
export function DownloadLink({ url, fileName = 'siloreal-image' }: DownloadLinkProps) {
  if (!url) {
    console.error('[SiloReal][DOWNLOAD-FILE] No URL provided');
    return null;
  }
  const ext = url.match(/\.([^./?#]+)($|\?|#)/)?.at(1);

  const handleDownload = () => {
    fetch(url, {
      method: 'GET',
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `${fileName}.${ext}`;
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Error fetching the file:', error);
        console.warn('Refetching...');
        fetch(url, {
          method: 'GET',
          cache: 'no-cache',
        })
          .then((response) => response.blob())
          .then((blob) => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName}.${ext}`;
            document.body.appendChild(link);

            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            console.info('Refetched successfully.');
          })
          .catch((error) => {
            console.error('Error (again) fetching the file:', error);
          });
      });
  };

  return (
    <Box
      sx={{
        width: '44px',
        height: '44px',
        borderRadius: '14px',
        bgcolor: '#2196F3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: '#22a7f0',
          transform: 'scale(1.1)',
          boxShadow: '0 6px 16px rgba(33, 150, 243, 0.6)',
        },
      }}
      onClick={handleDownload}
    >
      <img
        src={DownloadIcon}
        alt="download"
        style={{ width: '20px', height: '20px', filter: 'brightness(0) invert(1)' }}
      />
    </Box>
  );
}
